import { useEffect, useState } from "react";
import moment from "moment";
import { Default } from "../../components/default/Default";
import PostRequest from "../../services/PostRequest";
import { McaSegmentedControl, McaEmpty } from "../../components/mca";
import { showToaster, numberFormatter } from "../../common/common";
import "./usage.css";

// ============================================================================
// INLINE SVG CHART COMPONENTS (data-driven, no external library)
// ============================================================================

/**
 * LineAreaChart — Requests over time (line + area chart)
 * Computes SVG paths dynamically from data array.
 */
function LineAreaChart({ data }) {
  if (!data || data.length === 0) {
    return (
      <svg viewBox="0 0 720 280" role="img" aria-label="Requests over time line chart">
        <text x="360" y="140" textAnchor="middle" fill="var(--mca-muted)">No data</text>
      </svg>
    );
  }

  // Extract request counts from data array
  // Expected: [{"created_at": "2026-06-01", "count": 1234}, ...]
  const counts = data.map(d => parseInt(d.count) || 0);
  const maxCount = Math.max(...counts, 1);
  
  // SVG dimensions
  const WIDTH = 720;
  const HEIGHT = 280;
  const MARGIN_LEFT = 48;
  const MARGIN_RIGHT = 48;
  const MARGIN_TOP = 40;
  const MARGIN_BOTTOM = 40;
  
  const chartWidth = WIDTH - MARGIN_LEFT - MARGIN_RIGHT;
  const chartHeight = HEIGHT - MARGIN_TOP - MARGIN_BOTTOM;
  
  // Compute x,y for each point
  const points = counts.map((count, i) => {
    const x = MARGIN_LEFT + (i / (counts.length - 1 || 1)) * chartWidth;
    const y = MARGIN_TOP + chartHeight - (count / maxCount) * chartHeight;
    return { x, y, count };
  });
  
  // Build path strings
  const areaPath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`)
    .join(' ') + ` L${MARGIN_LEFT + chartWidth},${MARGIN_TOP + chartHeight} L${MARGIN_LEFT},${MARGIN_TOP + chartHeight} Z`;
  
  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`)
    .join(' ');
  
  // Y-axis labels (5 gridlines)
  const yLabels = [
    { value: maxCount, y: MARGIN_TOP },
    { value: (maxCount * 3) / 4, y: MARGIN_TOP + chartHeight * 0.25 },
    { value: (maxCount * 2) / 4, y: MARGIN_TOP + chartHeight * 0.5 },
    { value: maxCount / 4, y: MARGIN_TOP + chartHeight * 0.75 },
    { value: 0, y: MARGIN_TOP + chartHeight },
  ];
  
  // X-axis labels (up to 4 date markers)
  const xLabels = [];
  // Guard against step === 0: when counts.length <= 3, floor((n-1)/3) is 0 and
  // `i += 0` loops forever, freezing the tab. This is what hung the page on any
  // range whose month had only 1–3 data points (e.g. "90 days" -> March).
  const step = Math.max(1, Math.floor((counts.length - 1) / 3));
  for (let i = 0; i < counts.length; i += step) {
    const date = data[i]?.created_at || '';
    xLabels.push({ date, x: points[i]?.x || MARGIN_LEFT });
  }
  
  return (
    <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} role="img" aria-label="Requests over time line chart">
      <defs>
        <linearGradient id="mcaAreaGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--mca-primary)" stopOpacity="0.22" />
          <stop offset="100%" stopColor="var(--mca-primary)" stopOpacity="0" />
        </linearGradient>
      </defs>
      
      {/* Gridlines */}
      <g className="ch-grid">
        {yLabels.map((label, i) => (
          <line key={`grid-${i}`} x1={MARGIN_LEFT} y1={label.y} x2={MARGIN_LEFT + chartWidth} y2={label.y} />
        ))}
      </g>
      
      {/* Y-axis labels */}
      <g className="ch-axis" textAnchor="end">
        {yLabels.map((label, i) => (
          <text key={`y-${i}`} x={MARGIN_LEFT - 8} y={label.y + 4}>
            {label.value < 1000 ? label.value : (label.value / 1000).toFixed(1) + 'k'}
          </text>
        ))}
      </g>
      
      {/* Area fill */}
      <path className="ch-area" d={areaPath} />
      
      {/* Line */}
      <path className="ch-line" d={linePath} />
      
      {/* Dot on last point */}
      {points.length > 0 && (
        <circle className="ch-dot" cx={points[points.length - 1].x} cy={points[points.length - 1].y} r="4.5" />
      )}
      
      {/* X-axis labels */}
      <g className="ch-axis" textAnchor="middle">
        {xLabels.map((label, i) => (
          <text key={`x-${i}`} x={label.x} y={MARGIN_TOP + chartHeight + 28}>
            {moment(label.date, 'YYYY-MM-DD', true).format('MMM D')}
          </text>
        ))}
      </g>
    </svg>
  );
}

/**
 * BarChart — Requests by domain
 */
function BarChart({ data }) {
  if (!data || data.length === 0) {
    return (
      <svg viewBox="0 0 320 200" role="img" aria-label="Requests by domain bar chart">
        <text x="160" y="100" textAnchor="middle" fill="var(--mca-muted)">No data</text>
      </svg>
    );
  }
  
  // Take top 5 domains
  const topDomains = data.slice(0, 5);
  const maxCount = Math.max(...topDomains.map(d => parseInt(d.count) || 0), 1);
  
  const WIDTH = 320;
  const HEIGHT = 200;
  const chartHeight = 150;
  const barWidth = 42;
  const barSpacing = 66;
  
  return (
    <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} role="img" aria-label="Requests by domain bar chart">
      {/* Gridlines */}
      <g className="ch-grid">
        <line x1="0" y1="50" x2="320" y2="50" />
        <line x1="0" y1="100" x2="320" y2="100" />
        <line x1="0" y1="150" x2="320" y2="150" />
      </g>
      
      {/* Bars */}
      {topDomains.map((domain, i) => {
        const count = parseInt(domain.count) || 0;
        const height = (count / maxCount) * chartHeight;
        const y = 200 - height;
        const x = 14 + i * barSpacing;
        const isSoft = i >= 2; // Last 3 bars are soft color
        
        return (
          <rect
            key={`bar-${i}`}
            className={`ch-bar ${isSoft ? 'ch-bar--soft' : ''}`}
            x={x}
            y={y}
            width={barWidth}
            height={height}
            rx="5"
          />
        );
      })}
      
      {/* X-axis labels (domain names) */}
      <g className="ch-axis" textAnchor="middle">
        {topDomains.map((domain, i) => {
          const label = domain.domain
            ? domain.domain.split('.').slice(0, 1).join('')  // Just get first part
            : `D${i}`;
          const x = 35 + i * barSpacing;
          return (
            <text key={`x-${i}`} x={x} y="197">
              {label.substring(0, 10)}
            </text>
          );
        })}
      </g>
    </svg>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const Usage = () => {
  const [dateRange, setDateRange] = useState('30'); // '7', '30', '90'
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalRequests: 0,
    totalDelta: null, // % vs previous period (real, null = no prior data)
    creditsUsed: 0,
    creditsTotal: 0,
  });

  /**
   * Load data based on date range
   * Fetches from /users/uses/group-by-date (time series) and other endpoints
   */
  const loadData = async (range) => {
    setLoading(true);
    try {
      const days = parseInt(range, 10) || 30;
      // UTC is the timezone of record: server TIME_ZONE='UTC', so TruncDate
      // buckets each row by its UTC day. Build the rolling window + dense grid
      // in UTC so the grid keys match the server's date keys exactly (no
      // midnight off-by-one for AU users).
      const start = moment.utc().subtract(days - 1, 'days');
      const end = moment.utc();
      const startStr = start.format('YYYY-MM-DD');
      const endStr = end.format('YYYY-MM-DD');

      // Real credit balance — same source as Dashboard + Plan & Payment.
      let creditsUsed = 0;
      let creditsTotal = 0;
      try {
        const credit = await PostRequest('/users/credits/');
        creditsUsed = Number(credit?.used_credits ?? 0);
        creditsTotal = Number(credit?.available_credits ?? 0);
      } catch (e) {
        console.log('Credits unavailable:', e.message);
      }

      // Time series for the rolling window (drives the chart + total requests).
      const tsResponse = await PostRequest('/users/uses/group-by-date', {
        start_date: startStr,
        end_date: endStr,
      });
      const serverRows = tsResponse || [];
      const totalRequests = serverRows.reduce(
        (sum, d) => sum + (parseInt(d.count) || 0),
        0
      );

      // Zero-fill into a dense daily series across [start, end] (UTC) so the
      // chart is a true timeline and sparse data can't distort it. Match by
      // exact YYYY-MM-DD string equality.
      const byDay = new Map(
        serverRows.map((d) => [String(d.created_at).slice(0, 10), parseInt(d.count) || 0])
      );
      const dense = [];
      for (let i = 0; i < days; i++) {
        const dayStr = moment.utc(start).add(i, 'days').format('YYYY-MM-DD');
        dense.push({ created_at: dayStr, count: byDay.get(dayStr) || 0 });
      }
      setTimeSeriesData(dense);

      // Delta vs the immediately-preceding, disjoint N-day window.
      let totalDelta = null;
      try {
        const prevEnd = moment.utc(start).subtract(1, 'days');
        const prevStart = moment.utc(prevEnd).subtract(days - 1, 'days');
        const prevResponse = await PostRequest('/users/uses/group-by-date', {
          start_date: prevStart.format('YYYY-MM-DD'),
          end_date: prevEnd.format('YYYY-MM-DD'),
        });
        const prevTotal = (prevResponse || []).reduce(
          (sum, d) => sum + (parseInt(d.count) || 0),
          0
        );
        if (prevTotal > 0) {
          totalDelta = Math.round(((totalRequests - prevTotal) / prevTotal) * 1000) / 10;
        }
      } catch (e) {
        /* no prior-period data — leave delta null */
      }

      setStats({ totalRequests, totalDelta, creditsUsed, creditsTotal });
    } catch (error) {
      console.error('Error loading usage data:', error);
      showToaster('Failed to load usage data: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load initial data
  useEffect(() => {
    loadData(dateRange);
  }, [dateRange]);

  // Handle date range change
  const handleDateRangeChange = (range) => {
    setDateRange(range);
  };

  return (
    <Default>
      <div className="mca-content">
        {/* Page header */}
        <div className="us-head">
          <div>
            <h1 style={{ fontSize: '26px', fontWeight: '700', letterSpacing: '-0.02em' }}>
              API Usage
            </h1>
            <p style={{ color: 'var(--mca-muted)', fontSize: '14px', margin: '6px 0 0' }}>
              Track request volume, success rate, and credit consumption.
            </p>
          </div>
          
          {/* Date range filter */}
          <McaSegmentedControl
            options={[
              { id: '7', label: '7 days' },
              { id: '30', label: '30 days' },
              { id: '90', label: '90 days' },
            ]}
            value={dateRange}
            onChange={handleDateRangeChange}
          />
        </div>

        {/* Stats cards */}
        <div className="us-stats">
          <div className="mca-card stat">
            <div className="stat__label">Total requests</div>
            <div className="stat__value">{numberFormatter.format(stats.totalRequests)}</div>
            {stats.totalDelta === null ? (
              <div className="stat__delta" style={{ color: 'var(--mca-muted)' }}>
                vs prev period
              </div>
            ) : (
              <div
                className="stat__delta"
                style={{ color: stats.totalDelta >= 0 ? 'var(--mca-success)' : 'var(--mca-danger)' }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  {stats.totalDelta >= 0 ? (
                    <>
                      <path d="M7 17 17 7" />
                      <path d="M9 7h8v8" />
                    </>
                  ) : (
                    <>
                      <path d="M7 7 17 17" />
                      <path d="M9 17h8V9" />
                    </>
                  )}
                </svg>
                {Math.abs(stats.totalDelta)}% vs prev period
              </div>
            )}
          </div>

          <div className="mca-card stat">
            <div className="stat__label">Success rate</div>
            <div className="stat__value">—</div>
            <div className="stat__delta" style={{ color: 'var(--mca-muted)' }}>
              Not tracked yet
            </div>
          </div>

          <div className="mca-card stat">
            <div className="stat__label">Credits used</div>
            <div className="stat__value">{numberFormatter.format(stats.creditsUsed)}</div>
            <div className="stat__delta" style={{ color: 'var(--mca-muted)' }}>
              {stats.creditsTotal > 0
                ? `of ${numberFormatter.format(stats.creditsTotal)} this cycle`
                : 'this cycle'}
            </div>
          </div>

          <div className="mca-card stat">
            <div className="stat__label">Avg response</div>
            <div className="stat__value">—</div>
            <div className="stat__delta" style={{ color: 'var(--mca-muted)' }}>
              Not tracked yet
            </div>
          </div>
        </div>

        {/* Chart */}
        {loading ? (
          <div
            className="mca-card mca-card--pad"
            style={{ textAlign: 'center', padding: '64px 20px', color: 'var(--mca-muted)' }}
          >
            Loading…
          </div>
        ) : stats.totalRequests > 0 ? (
          <div className="mca-card mca-card--pad">
            <div className="chart-head">
              <div className="mca-card__title">Requests over time</div>
              <div className="mca-chart-legend">
                <span>
                  <i style={{ background: 'var(--mca-primary)' }}></i>
                  Primary
                </span>
              </div>
            </div>
            <div className="mca-chart">
              <LineAreaChart data={timeSeriesData} />
            </div>
          </div>
        ) : (
          <McaEmpty
            title="No usage data"
            description="No requests found for this period. Your usage will appear here once you start making API requests."
            minHeight={true}
          />
        )}
      </div>
    </Default>
  );
};

export default Usage;